import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '@/lib/api';

const AuthContext = createContext(null);

// How often (ms) to re-fetch /auth/me while the tab is visible so newly granted
// permissions land without forcing the user to log out and log back in.
const PERM_REFRESH_MS = 30_000;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const pollRef = useRef(null);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me');
      setUser(data.user);
      setPermissions(data.permissions || {});
      return data;
    } catch {
      setUser(false);
      setPermissions({});
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Refresh just the permissions map without flipping the loading flag —
  // used both by the visibility poll and by admins right after saving grants.
  const refreshPermissions = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me');
      if (data?.user) {
        setUser(data.user);
        setPermissions(data.permissions || {});
      }
      return data?.permissions || {};
    } catch {
      return null;
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  // Live poll for permission changes: refresh on tab focus AND every 30s while visible.
  useEffect(() => {
    if (!user || user === false) return undefined;
    const tick = () => {
      if (document.visibilityState === 'visible') refreshPermissions();
    };
    pollRef.current = window.setInterval(tick, PERM_REFRESH_MS);
    window.addEventListener('focus', tick);
    document.addEventListener('visibilitychange', tick);
    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
      window.removeEventListener('focus', tick);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [user, refreshPermissions]);

  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    // OTP flow - doesn't set user yet
    return data;
  };

  const verifyLoginOtp = async (tempToken, otpCode) => {
    const { data } = await api.post('/auth/verify-login-otp', { temp_token: tempToken, otp_code: otpCode });
    setUser(data.user);
    return data;
  };

  const register = async (email, password, name, phone) => {
    const { data } = await api.post('/auth/register', { email, password, name, phone });
    return data;
  };

  const logout = async () => {
    await api.post('/auth/logout');
    setUser(false);
    setPermissions({});
  };

  return (
    <AuthContext.Provider value={{ user, permissions, loading, login, verifyLoginOtp, register, logout, checkAuth, refreshPermissions }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
